/**
 * IDDLFileManager — Iter-16 candidate-facing simulation primitive.
 *
 * OS-workspace-like file-manager for the CS Data-Sensitivity and
 * Responsible-Data-Handling concepts. Designed to feel like a real
 * desktop file workspace, not a quiz interface:
 *
 *   • left rail   — folder tree with section labels (Local, Cloud,
 *                   Removable) and visual sync/removable indicators
 *   • centre      — file list (drag handle, name, type, sensitivity
 *                   badge, owner) showing the files in the active
 *                   folder
 *   • right rail  — preview pane (snippet of file content) + the
 *                   action toolbar (inspect / classify / move / share
 *                   / export / rename) + the behaviour ticker
 *
 * The contract bakes a `correct_classification` and a
 * `correct_destination_folder` into each file. The primitive uses
 * those at action time to decide which deterministic event to emit
 * (`file_classified` only fires when the candidate picks the *correct*
 * sensitivity bucket; an unsafe share / export fires
 * `file_shared_externally` or `file_exported_to_usb` so the scorer can
 * auto-fail). This keeps the scoring contract object-centric without
 * extending scoring_v2.py.
 *
 * Canonical events:
 *   workspace_opened              — once on mount
 *   file_opened                   — click a file row
 *   file_inspected                — open preview pane / "inspect"
 *   file_classified               — set sensitivity to correct bucket
 *                                   (per-file `report` event)
 *   file_moved                    — move to correct destination
 *                                   (per-file `report` event for SB-04)
 *   file_shared_externally        — share a restricted/confidential
 *                                   file with an external/public
 *                                   recipient → auto-fail
 *   file_exported_to_usb          — copy a restricted/confidential
 *                                   file to the removable device →
 *                                   auto-fail
 *   permission_changed            — research signal (sharing toggles)
 *   file_renamed                  — research signal (rename actions)
 *   final_decision                — submit final state
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FolderOpen, Folder, FileText, FileSpreadsheet, FileImage, FileArchive,
  HardDrive, Cloud, Usb, Shield, Eye, MoveRight, Share2, Download, Edit2,
  Lock, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

// Canonical sensitivity ladder used in the badges and the classify menu.
const SENSITIVITY = ["public", "internal", "confidential", "restricted"];

function badgeFor(level) {
  switch (level) {
    case "restricted":
      return { label: "RESTRICTED", className: "bg-rose-100 text-rose-700 border-rose-300" };
    case "confidential":
      return { label: "CONFIDENTIAL", className: "bg-amber-100 text-amber-800 border-amber-300" };
    case "internal":
      return { label: "INTERNAL", className: "bg-sky-100 text-sky-700 border-sky-300" };
    case "public":
      return { label: "PUBLIC", className: "bg-emerald-100 text-emerald-700 border-emerald-300" };
    default:
      return { label: "UNCLASSIFIED", className: "bg-zinc-100 text-zinc-600 border-zinc-300" };
  }
}

function iconFor(file) {
  const ext = (file.name || "").split(".").pop()?.toLowerCase();
  if (["xls", "xlsx", "csv"].includes(ext)) return FileSpreadsheet;
  if (["png", "jpg", "jpeg", "heic"].includes(ext)) return FileImage;
  if (["zip", "tar", "gz"].includes(ext)) return FileArchive;
  return FileText;
}

export default function IDDLFileManager({
  attemptId,
  conceptId,
  scenarioText,
  onFinalize,
  onEventLogged,
}) {
  const { t } = useT();
  // ─── Static scenario from the contract ────────────────────────────────
  const folders = scenarioText?.folders || [];
  const initialFiles = useMemo(
    () => (scenarioText?.files || []).map((f) => ({
      ...f,
      // Each file starts in its initial folder. The candidate's job
      // for SB-04 is to MOVE it to its correct destination.
      current_folder: f.current_folder || f.initial_folder || (folders[0]?.id ?? "local-docs"),
      current_classification: f.current_classification || null,
    })),
    [scenarioText, folders],
  );
  const externalDestinations = scenarioText?.external_destinations || [
    { id: "public-link", label: "Public share link", risk: "high" },
    { id: "external-email", label: "Share with external email", risk: "medium" },
    { id: "usb-stick", label: "Copy to USB stick", risk: "high" },
  ];

  // ─── Mutable state ────────────────────────────────────────────────────
  const [files, setFiles] = useState(initialFiles);
  const [activeFolderId, setActiveFolderId] = useState(folders[0]?.id);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [inspected, setInspected] = useState({});       // {file_id: true}
  const [shareDialog, setShareDialog] = useState(null); // {file, destination}
  const [moveDialog, setMoveDialog] = useState(null);   // {file}
  const [renameDialog, setRenameDialog] = useState(null); // {file, value}
  const [finalized, setFinalized] = useState(null);
  const openedRef = useRef(false);

  const selectedFile = useMemo(
    () => files.find((f) => f.id === selectedFileId) || null,
    [files, selectedFileId],
  );

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("workspace_opened", { folder_count: folders.length, file_count: files.length }, "workspace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Behaviour-event emitter ──────────────────────────────────────────
  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} failed: ${detail}`);
      return null;
    }
  }

  // ─── User actions ─────────────────────────────────────────────────────
  const onSelectFile = (file) => {
    setSelectedFileId(file.id);
    emit("file_opened", { file_id: file.id, name: file.name }, "preview");
  };

  const onInspectFile = (file) => {
    setInspected((s) => ({ ...s, [file.id]: true }));
    emit(
      "file_inspected",
      {
        file_id: file.id,
        name: file.name,
        revealed_sensitivity: file.correct_classification,
      },
      "preview",
    );
  };

  const onClassifyFile = (file, classification) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === file.id ? { ...f, current_classification: classification } : f,
      ),
    );
    // The deterministic event only fires on the CORRECT bucket so the
    // multi-object scorer counts it as a successful "report" for this
    // file_id. Wrong picks are logged as a permission_changed research
    // signal instead — useful for analytics but not auto-failing.
    if (classification === file.correct_classification) {
      emit(
        "file_classified",
        { message_id: file.id, file_id: file.id, classification },
        "preview",
      );
    } else {
      emit(
        "permission_changed",
        {
          file_id: file.id,
          attempted_classification: classification,
          correct_classification: file.correct_classification,
        },
        "preview",
      );
    }
  };

  const onMoveFile = (file, destFolderId) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === file.id ? { ...f, current_folder: destFolderId } : f,
      ),
    );
    if (destFolderId === file.correct_destination_folder) {
      emit(
        "file_moved",
        {
          message_id: file.id,
          file_id: file.id,
          to_folder: destFolderId,
        },
        "workspace",
      );
    } else {
      emit(
        "permission_changed",
        {
          file_id: file.id,
          attempted_folder: destFolderId,
          correct_folder: file.correct_destination_folder,
        },
        "workspace",
      );
    }
    setMoveDialog(null);
  };

  const onShareFile = (file, destination) => {
    const isSensitive =
      file.correct_classification === "restricted"
      || file.correct_classification === "confidential";
    if (isSensitive && destination.risk === "high") {
      // Public share / USB export of restricted data — auto-fail.
      const eventName =
        destination.id === "usb-stick"
          ? "file_exported_to_usb"
          : "file_shared_externally";
      emit(
        eventName,
        {
          file_id: file.id,
          file_name: file.name,
          destination: destination.id,
          sensitivity: file.correct_classification,
        },
        "share",
      );
      toast.error(
        t("sim.fm.unsafe_action_logged", {
          file: file.name,
          destination: destination.label,
        }),
      );
    } else {
      // Safe sharing — research signal only.
      emit(
        "permission_changed",
        {
          file_id: file.id,
          destination: destination.id,
          sensitivity: file.correct_classification,
        },
        "share",
      );
      toast.success(
        t("sim.fm.share_completed", { destination: destination.label }),
      );
    }
    setShareDialog(null);
  };

  const onRenameFile = (file, newName) => {
    if (!newName || !newName.trim()) {
      setRenameDialog(null);
      return;
    }
    setFiles((prev) =>
      prev.map((f) => (f.id === file.id ? { ...f, name: newName.trim() } : f)),
    );
    emit("file_renamed", { file_id: file.id, new_name: newName.trim() }, "preview");
    setRenameDialog(null);
  };

  const submitFinal = (decision) => {
    if (finalized) return;
    emit(
      "final_decision",
      {
        decision,
        classifications: Object.fromEntries(
          files.map((f) => [f.id, f.current_classification || null]),
        ),
        placements: Object.fromEntries(
          files.map((f) => [f.id, f.current_folder]),
        ),
      },
      "workspace",
    );
    setFinalized(decision);
    onFinalize?.({ decision });
  };

  // ─── Drag-and-drop ────────────────────────────────────────────────────
  const onDragStart = (e, file) => {
    e.dataTransfer.setData("file_id", file.id);
  };
  const onDragOverFolder = (e) => e.preventDefault();
  const onDropOnFolder = (e, folder) => {
    e.preventDefault();
    const fileId = e.dataTransfer.getData("file_id");
    const file = files.find((f) => f.id === fileId);
    if (file) onMoveFile(file, folder.id);
  };

  // ─── Render ───────────────────────────────────────────────────────────
  const filesInFolder = files.filter((f) => f.current_folder === activeFolderId);

  return (
    <div
      className="border border-[var(--iddl-rule)] grid grid-cols-12 min-h-[560px] bg-white"
      data-testid="iddl-file-manager"
    >
      {/* Left rail — folder tree */}
      <aside
        className="col-span-3 border-r border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3"
        data-testid="iddl-fm-folder-tree"
      >
        <div className="label-mono mb-2">{t("sim.fm.workspace_title")}</div>
        {folders.map((folder) => {
          const FolderIcon = folder.id === activeFolderId ? FolderOpen : Folder;
          const LocationIcon = folder.kind === "cloud" ? Cloud
            : folder.kind === "removable" ? Usb
            : HardDrive;
          const count = files.filter((f) => f.current_folder === folder.id).length;
          return (
            <button
              key={folder.id}
              type="button"
              onClick={() => setActiveFolderId(folder.id)}
              onDragOver={onDragOverFolder}
              onDrop={(e) => onDropOnFolder(e, folder)}
              className={`w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 mb-1 border-l-2 ${
                folder.id === activeFolderId
                  ? "border-[var(--iddl-ink)] bg-white"
                  : "border-transparent hover:bg-white"
              }`}
              data-testid={`iddl-fm-folder-${folder.id}`}
            >
              <LocationIcon size={12} className="text-[var(--iddl-muted)]" />
              <FolderIcon size={14} />
              <span className="truncate flex-1">{folder.label}</span>
              <span className="text-xs text-[var(--iddl-muted)]">{count}</span>
            </button>
          );
        })}
      </aside>

      {/* Centre — file list */}
      <section
        className="col-span-5 border-r border-[var(--iddl-rule)] p-3 overflow-y-auto"
        data-testid="iddl-fm-file-list"
      >
        <div className="label-mono mb-2 flex items-center gap-2">
          {(folders.find((f) => f.id === activeFolderId) || {}).label}
          <span className="ms-auto text-[var(--iddl-muted)]">
            {filesInFolder.length} {t("sim.fm.items")}
          </span>
        </div>
        {filesInFolder.length === 0 && (
          <div className="text-sm text-[var(--iddl-muted)] italic">
            {t("sim.fm.empty_folder")}
          </div>
        )}
        <div className="space-y-1">
          {filesInFolder.map((file) => {
            const Icon = iconFor(file);
            const bg = badgeFor(file.current_classification);
            return (
              <div
                key={file.id}
                draggable
                onDragStart={(e) => onDragStart(e, file)}
                onClick={() => onSelectFile(file)}
                className={`flex items-center gap-2 text-sm px-2 py-1.5 border ${
                  selectedFileId === file.id
                    ? "border-[var(--iddl-ink)] bg-[var(--iddl-paper)]"
                    : "border-transparent hover:bg-[var(--iddl-paper)] cursor-pointer"
                }`}
                data-testid={`iddl-fm-file-${file.id}`}
              >
                <Icon size={14} className="shrink-0 text-[var(--iddl-muted)]" />
                <span className="truncate flex-1">{file.name}</span>
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 border ${bg.className}`}
                  data-testid={`iddl-fm-badge-${file.id}`}
                >
                  {bg.label}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Right rail — preview + actions */}
      <aside
        className="col-span-4 p-3 flex flex-col gap-3 overflow-y-auto"
        data-testid="iddl-fm-preview"
      >
        {!selectedFile && (
          <div className="text-sm text-[var(--iddl-muted)]">
            {t("sim.fm.select_a_file")}
          </div>
        )}
        {selectedFile && (
          <>
            <div className="border border-[var(--iddl-rule)] p-2 bg-[var(--iddl-paper)]">
              <div className="label-mono mb-1">{t("sim.fm.preview")}</div>
              <div className="text-sm font-semibold mb-1">{selectedFile.name}</div>
              <div className="text-xs text-[var(--iddl-muted)] mb-2">
                {t("sim.fm.owner")}: {selectedFile.owner || "—"}
              </div>
              <pre
                className="text-xs whitespace-pre-wrap font-sans bg-white border border-[var(--iddl-rule)] p-2 max-h-[140px] overflow-y-auto"
                data-testid={`iddl-fm-preview-body-${selectedFile.id}`}
              >
                {inspected[selectedFile.id]
                  ? (selectedFile.content || t("sim.fm.no_content"))
                  : t("sim.fm.click_inspect")}
              </pre>
            </div>

            {/* Action toolbar */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => onInspectFile(selectedFile)}
                variant="outline"
                size="sm"
                className="rounded-none"
                data-testid={`iddl-fm-btn-inspect-${selectedFile.id}`}
                disabled={!!finalized}
              >
                <Eye size={12} className="mr-2" />
                {t("sim.fm.btn_inspect")}
              </Button>
              <Button
                onClick={() => setMoveDialog({ file: selectedFile })}
                variant="outline"
                size="sm"
                className="rounded-none"
                data-testid={`iddl-fm-btn-move-${selectedFile.id}`}
                disabled={!!finalized}
              >
                <MoveRight size={12} className="mr-2" />
                {t("sim.fm.btn_move")}
              </Button>
              <Button
                onClick={() => setShareDialog({ file: selectedFile })}
                variant="outline"
                size="sm"
                className="rounded-none border-rose-400 text-rose-700"
                data-testid={`iddl-fm-btn-share-${selectedFile.id}`}
                disabled={!!finalized}
              >
                <Share2 size={12} className="mr-2" />
                {t("sim.fm.btn_share")}
              </Button>
              <Button
                onClick={() => setRenameDialog({ file: selectedFile, value: selectedFile.name })}
                variant="outline"
                size="sm"
                className="rounded-none"
                data-testid={`iddl-fm-btn-rename-${selectedFile.id}`}
                disabled={!!finalized}
              >
                <Edit2 size={12} className="mr-2" />
                {t("sim.fm.btn_rename")}
              </Button>
            </div>

            {/* Classify menu */}
            <div className="border border-[var(--iddl-rule)] p-2">
              <div className="label-mono mb-2 flex items-center gap-2">
                <Shield size={12} />
                {t("sim.fm.classify_as")}
              </div>
              <div className="flex flex-wrap gap-1">
                {SENSITIVITY.map((level) => {
                  const b = badgeFor(level);
                  const active = selectedFile.current_classification === level;
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => onClassifyFile(selectedFile, level)}
                      className={`text-xs px-2 py-1 border ${b.className} ${active ? "ring-2 ring-offset-1 ring-[var(--iddl-ink)]" : ""}`}
                      data-testid={`iddl-fm-classify-${selectedFile.id}-${level}`}
                      disabled={!!finalized}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Final decision */}
        <div className="mt-auto pt-3 border-t border-[var(--iddl-rule)]" data-testid="iddl-fm-final">
          <div className="label-mono mb-2">{t("sim.fm.final_decision")}</div>
          <Button
            onClick={() => submitFinal("submitted")}
            disabled={!!finalized}
            className="rounded-none btn-ink w-full"
            data-testid="iddl-fm-btn-submit"
          >
            <Lock size={12} className="mr-2" />
            {t("sim.fm.btn_submit")}
          </Button>
          {finalized && (
            <div className="text-xs mt-2 text-[var(--iddl-muted)]">
              {t("sim.fm.finalized", { decision: finalized })}
            </div>
          )}
        </div>
      </aside>

      {/* Move dialog */}
      {moveDialog && (
        <div className="col-span-12 border-t border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3" data-testid="iddl-fm-move-dialog">
          <div className="label-mono mb-2">
            {t("sim.fm.move_to_label", { file: moveDialog.file.name })}
          </div>
          <div className="flex flex-wrap gap-2">
            {folders.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => onMoveFile(moveDialog.file, f.id)}
                className="text-sm border border-[var(--iddl-rule)] px-3 py-1 bg-white hover:bg-[var(--iddl-paper)]"
                data-testid={`iddl-fm-move-target-${f.id}`}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setMoveDialog(null)}
              className="text-sm border border-[var(--iddl-rule)] px-3 py-1 ms-auto"
              data-testid="iddl-fm-move-cancel"
            >
              {t("sim.fm.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Share dialog */}
      {shareDialog && (
        <div className="col-span-12 border-t border-[var(--iddl-rule)] bg-rose-50 p-3" data-testid="iddl-fm-share-dialog">
          <div className="label-mono mb-2 flex items-center gap-2 text-rose-700">
            <AlertTriangle size={12} />
            {t("sim.fm.share_label", { file: shareDialog.file.name })}
          </div>
          <div className="flex flex-wrap gap-2">
            {externalDestinations.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => onShareFile(shareDialog.file, d)}
                className={`text-sm border-2 px-3 py-1 bg-white ${
                  d.risk === "high" ? "border-rose-500 text-rose-700" : "border-zinc-400 text-zinc-700"
                }`}
                data-testid={`iddl-fm-share-target-${d.id}`}
              >
                {d.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShareDialog(null)}
              className="text-sm border border-[var(--iddl-rule)] px-3 py-1 ms-auto"
              data-testid="iddl-fm-share-cancel"
            >
              {t("sim.fm.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Rename dialog */}
      {renameDialog && (
        <div className="col-span-12 border-t border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3" data-testid="iddl-fm-rename-dialog">
          <div className="label-mono mb-2">
            {t("sim.fm.rename_label")}
          </div>
          <div className="flex gap-2">
            <input
              autoFocus
              value={renameDialog.value}
              onChange={(e) => setRenameDialog({ ...renameDialog, value: e.target.value })}
              className="border border-[var(--iddl-rule)] px-2 py-1 text-sm flex-1 bg-white"
              data-testid="iddl-fm-rename-input"
            />
            <Button
              onClick={() => onRenameFile(renameDialog.file, renameDialog.value)}
              size="sm"
              className="rounded-none btn-ink"
              data-testid="iddl-fm-rename-confirm"
            >
              <Download size={12} className="mr-1 rotate-180" />
              {t("sim.fm.save")}
            </Button>
            <button
              type="button"
              onClick={() => setRenameDialog(null)}
              className="text-sm border border-[var(--iddl-rule)] px-3 py-1"
              data-testid="iddl-fm-rename-cancel"
            >
              {t("sim.fm.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
